import React from 'react';
import { useFleetStatus } from '../hooks/useFleetStatus';
import { useDeployments } from '../hooks/useDeployments';
import { useRepositories } from '../hooks/useRepositories';
import { useTickets } from '../hooks/useTickets';
import { LayoutDashboard, FolderGit2, Rocket, Ticket, Activity, TrendingUp } from 'lucide-react';
import clsx from 'clsx';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  trend?: {
    value: number;
    label: string;
  };
}

function StatCard({ title, value, icon: Icon, color, trend }: StatCardProps) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
          {trend && (
            <div className="flex items-center gap-1 mt-2">
              <TrendingUp className="h-4 w-4 text-green-600" />
              <span className="text-sm text-green-600">
                {trend.value > 0 ? '+' : ''}
                {trend.value}% {trend.label}
              </span>
            </div>
          )}
        </div>
        <div className={clsx('p-3 rounded-lg', color)}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
    </div>
  );
}

export function Dashboard() {
  const { fleetStatus, loading: fleetLoading } = useFleetStatus();
  const { deployments, loading: deploymentsLoading } = useDeployments();
  const { repositories, loading: reposLoading } = useRepositories();
  const { tickets, loading: ticketsLoading } = useTickets();

  const activeDeployments = deployments.filter(
    (d) => d.status === 'in_progress' || d.status === 'pending'
  ).length;

  const openTickets = tickets.filter((t) => t.status === 'open').length;
  const inProgressTickets = tickets.filter((t) => t.status === 'in-progress').length;

  const recentDeployments = deployments
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5);

  const recentTickets = tickets
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5);

  const loading = fleetLoading || deploymentsLoading || reposLoading || ticketsLoading;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <LayoutDashboard className="h-8 w-8 text-blue-600" />
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">Overview of your repository fleet</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Repositories"
          value={repositories.length}
          icon={FolderGit2}
          color="bg-blue-600"
        />
        <StatCard
          title="Active Deployments"
          value={activeDeployments}
          icon={Rocket}
          color="bg-green-600"
        />
        <StatCard
          title="Open Tickets"
          value={openTickets}
          icon={Ticket}
          color="bg-yellow-600"
        />
        <StatCard
          title="Fleet Health"
          value={fleetStatus?.workerHealth === 'healthy' ? '100%' : 'N/A'}
          icon={Activity}
          color="bg-purple-600"
        />
      </div>

      {/* Activity Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Deployments */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Rocket className="h-5 w-5 text-blue-600" />
            Recent Deployments
          </h2>
          {loading ? (
            <p className="text-gray-500">Loading...</p>
          ) : recentDeployments.length === 0 ? (
            <p className="text-gray-500">No deployments yet</p>
          ) : (
            <div className="space-y-3">
              {recentDeployments.map((deployment) => (
                <div
                  key={deployment.id}
                  className="flex items-start justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {deployment.featureName}
                    </p>
                    <p className="text-sm text-gray-600">
                      {deployment.targetRepos?.length || 0} repositories
                    </p>
                  </div>
                  <span
                    className={clsx(
                      'px-2 py-1 rounded text-xs font-medium capitalize',
                      deployment.status === 'completed' && 'bg-green-100 text-green-700',
                      deployment.status === 'in_progress' && 'bg-blue-100 text-blue-700',
                      deployment.status === 'failed' && 'bg-red-100 text-red-700',
                      deployment.status === 'pending' && 'bg-yellow-100 text-yellow-700'
                    )}
                  >
                    {deployment.status.replace('_', ' ')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Tickets */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Ticket className="h-5 w-5 text-blue-600" />
            Recent Tickets
          </h2>
          {loading ? (
            <p className="text-gray-500">Loading...</p>
          ) : recentTickets.length === 0 ? (
            <p className="text-gray-500">No tickets yet</p>
          ) : (
            <div className="space-y-3">
              {recentTickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className="flex items-start justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{ticket.title}</p>
                    <p className="text-sm text-gray-600">{ticket.repoName}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span
                      className={clsx(
                        'px-2 py-1 rounded text-xs font-medium capitalize',
                        ticket.status === 'open' && 'bg-blue-100 text-blue-700',
                        ticket.status === 'in-progress' && 'bg-yellow-100 text-yellow-700',
                        ticket.status === 'blocked' && 'bg-red-100 text-red-700',
                        ticket.status === 'review' && 'bg-purple-100 text-purple-700',
                        ticket.status === 'closed' && 'bg-green-100 text-green-700'
                      )}
                    >
                      {ticket.status}
                    </span>
                    {ticket.priority && (
                      <span
                        className={clsx(
                          'px-2 py-0.5 rounded text-xs font-medium',
                          ticket.priority === 'critical' && 'bg-red-100 text-red-700',
                          ticket.priority === 'high' && 'bg-orange-100 text-orange-700',
                          ticket.priority === 'medium' && 'bg-yellow-100 text-yellow-700',
                          ticket.priority === 'low' && 'bg-gray-100 text-gray-700'
                        )}
                      >
                        {ticket.priority}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Fleet Status */}
      {fleetStatus && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-600" />
            Fleet Status
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Repositories</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{fleetStatus.totalRepos}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Active Deployments</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {fleetStatus.activeDeployments}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Worker Health</p>
              <p
                className={clsx(
                  'text-2xl font-bold mt-1 capitalize',
                  fleetStatus.workerHealth === 'healthy' && 'text-green-600',
                  fleetStatus.workerHealth === 'degraded' && 'text-yellow-600',
                  fleetStatus.workerHealth === 'down' && 'text-red-600'
                )}
              >
                {fleetStatus.workerHealth}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
